/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { SerializedEditorState } from 'lexical';
import { TableColumn, ValueTable } from '../components/survey/form/input/table/InputInterface';
import { Option, ValueList } from '../slice/valueListSlice';

export enum UtrValueType {
  Number = 'number',
  Sample = 'sample',
  Percentage = 'percentage',
  Text = 'text',
  Date = 'date',
  ValueListMulti = 'valueListMulti', // MultiSelect
  ValueList = 'valueList', // Single Select
  NumericValueList = 'numericValueList', // Numeric Multi Checkbox
  TextValueList = 'textValueList', // Multi Checkbox
  Table = 'table',
}

export enum TableColumnType {
  Number = 'number',
  Text = 'text',
  Date = 'date',
  ValueList = 'valueList',
  ValueListMulti = 'valueListMulti',
  Percentage = 'percentage',
};

export enum DataPeriods {
  Monthly = 'monthly',
  Quarterly = 'quarterly',
  Yearly = 'yearly'
}

export const hasMinAndMaxColumnType = (columnType: TableColumnType, min: number | undefined, max: number | undefined) => {
  switch (columnType) {
    case TableColumnType.Number:
      return min === 0 && max === 100;
    case TableColumnType.Percentage:
      return min !== undefined && max !== undefined && min < max;
    default:
      return false;
  }
}

export const getTableColumnTypeNames = (type: TableColumnType, hasListId: boolean = false, col?: TableColumn) => {
  const names = {
    [TableColumnType.Number]: 'Number',
    [TableColumnType.Text]: 'Text',
    [TableColumnType.Date]: 'Date',
    [TableColumnType.Percentage]: 'Number',
    [TableColumnType.ValueListMulti]: 'Text Multiple-Choices',
    [TableColumnType.ValueList]: 'Text Multiple-Choice'
  };
  if (type === TableColumnType.Text && hasListId) {
    return names[TableColumnType.ValueList];
  }
  if (hasMinAndMaxColumnType(type, col?.validation?.min, col?.validation?.max)) {
    return 'Percentage';
  }
  return names[type];
}

export interface UniversalTrackerMin {
  _id: string;
  name: string;
  type: string;
  valueType?: string;
}

export interface Tags {
  cdsb: string[];
  tcfd: string[];
  pic: string[];
  ese: string[];
  sdg: string[];
  wef: string[];
  ungc: string[];
  sec: string[];
  see: string[];
  esg30?: string[];
}

export interface UniversalTrackerBlueprintMin extends UniversalTrackerMin {
  _id: string;
  code: string;
  typeCode: string;
  valueLabel: string;
  alternatives?: { [key: string]: Alternative | LanguageAlternative };
  tags?: Tags;
  valueValidation?: ValueValidation;
  valueType: string;
  typeTags?: string[];
  unit?: string;
}

export interface Alternative {
  name: string;
  valueLabel: string;
  alternativeType?: string;
  languageCode?: string;
  typeCode?: string;
  description?: string;
  instructions?: string;
  instructionsEditorState?: SerializedEditorState;
  instructionsLink?: string;
  evidenceInstructions?: string;
  sampleSizeLabel?: string;
  typeTags?: string[];
}

export interface LanguageAlternative extends Alternative {
  alternativeType: 'language';
  languageCode: string;
}

export interface ValueValidation {
  min?: number;
  max?: number;
  decimal?: number;
  valueList?: {
    list?: Option[];
    type: string;
    listId?: string;
    custom: any;
    allowCustomOptions?: Boolean;
  };
  table?: ValueTable;
}

interface Calculation {
  formula: string;
  useDisaggregations?: boolean;
  variables: {
    [key: string]: CalculationVariable;
  }
}

export interface Connection extends Calculation {
  score: number;
}

export interface UniversalTrackerPlain extends UniversalTrackerMin {
  _id: string;
  instructions?: string;
  instructionsEditorState?: SerializedEditorState;
  code: string;
  created: string;
  valueListOrdered?: any[];
  valueListTargets?: any[];
  description?: string;
  type: string;
  typeCode: string;
  typeTags?: string[];
  targetDirection?: string;
  valueLabel: string;
  valueType: string;
  name: string;
  ownerId?: string;
  unitType?: string;
  unit?: string;
  unitInput?: string;
  numberScaleInput?: string;
  numberScale?: string;
  valueValidation?: ValueValidation;
  alternatives?: { [key: string]: Alternative | LanguageAlternative };
  tags?: Tags;
  valueListOptions?: ValueList;
  tableColumnValueListOptions?: ValueList[];
  calculation?: Calculation;
  connections?: Connection[];
  instructionsLink?: string;
  evidenceInstructions?: string;
}

export interface CalculationVariable {
  code: string;
  valueAggregation?: string | null;
  valueListCode?: string;
}

export interface UniveresalTrackerGroup {
  _id: string;
  code: string;
  name: string;
  initiativeId: string;
  universalTrackers: string[];
}

export type ConnectionUtr = Pick<
  UniversalTrackerPlain,
  | '_id'
  | 'code'
  | 'name'
  | 'valueLabel'
  | 'type'
  | 'valueType'
  | 'valueValidation'
  | 'typeCode'
  | 'unit'
  | 'unitType'
  | 'numberScale'
>;

export interface EvidenceLink {
  url: string;
  description?: string;
}
